import React, { useContext } from 'react'
import DOMPurify from 'dompurify'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import * as Styled from './GeneralInformationCompare.styles'

// component stack
import { QsrIndicator } from 'components/molecules'
import { ComparedDataRow, NoCompareValue } from './partials'
import { BarGraph, BookmarkToggle, Icon } from 'components/atoms'

// libraries && helpers
import { getDetailURL } from 'settings/app/urlHelpers'
import { getReplacedString } from 'settings/app/helpers'
import { TextContext } from 'hocs/TextContextProvider/TextContextProvider'
import {
  bool,
  func,
  oneOf,
  shape,
  node,
  string,
  number,
  oneOfType,
  arrayOf,
  array,
} from 'prop-types'
import {
  BOOKABLE_TYPES,
  entryPointId,
  mappedTreatmentScores,
} from 'settings/app/constants'
import { RESULT_HAS_CONTRIBUTION } from 'containers/Compare/helpers/constants'
import {
  getContributionLabels,
  compareSingleProperty,
  compareIdentifier,
  compareArrayProperty,
} from 'containers/Compare/helpers/helpers'

/**
 * header component for compare
 * show basic information
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const GeneralInformationCompare = (props) => {
  const {
    resultType,
    data,
    onToggleBookmark,
    type,
    className = '',
    withOutTreatmentReason,
    mobile,
    disableToggleBookmark,
  } = props
  const textContext = useContext(TextContext)

  const state = useSelector((state) => state)
  const { compare, bookmarks } = state
  const { activeIds } = compare.activeElements
  const { results, generalTooltips: CompareTooltips } =
    compare.types[resultType]
  let comparingResults,
    qsrIdentifier = []
  const wrapperClass = `${ className } ${ mobile ? 'mobile' : '' } ${
    type === 'contact' ? 'noMarginTop' : ''
  }`.trim()

  const hasContactData =
    type === 'contact' && (data?.phone || data?.fax || data?.email || data?.web)

  // filter direct compared hospitals and max there max qsr values#
  if (resultType !== entryPointId.DOCTORS) {
    if (resultType === entryPointId.HOSPITALS) {
      qsrIdentifier = compare.types.hospitals.qsrIdentifier
    }
    comparingResults = results.filter(
      (result) => activeIds.includes(result.id) && result.id !== data.id
    )
  }

  // TODO check later when data will provided
  /**
   * return mapped data rows (careservices only)
   */
  // const getMappedServices = () => {
  //         if (compare.types[resultType].compareKeys.includes('services')) {
  //             mappedDataRows.push(<ComparedDataRow
  //                 bestValue={
  //                     compareSingleProperty('pricePerHour',
  //                         data.pricePerHour,
  //                         comparingResults,
  //                         true)
  //                 }
  //                 withOutTooltip={ true }
  //                 className="first-paragraph-margin"
  //             >
  //                 <>
  //                     <p>{ textContext['generalInformation.pricePerHour'] }</p>
  //                     <p>
  //                         <strong>{ data.pricePerHour }</strong>
  //                     </p>
  //                 </>
  //             </ComparedDataRow>)
  //         }
  //
  //     }

  /**
   * get transparency report
   * @returns {JSX.Element}
   */
  const getTransparencyReport = () =>
    data.transparencyReport === null ? (
      <NoCompareValue
        compareKey={ `${ textContext['compare.dataRow.noTransparencyReport'] }` }
      />
    ) : (
      <ComparedDataRow
        bestValue={ compareSingleProperty(
          'transparencyReport',
          data.transparencyReport,
          comparingResults,
          true
        ) }
        title={ textContext['generalInformation.transparencyReport'] }
        hint={
          CompareTooltips?.transparencyReport
            ? CompareTooltips.transparencyReport
            : ''
        }>
        <p>
          <strong>{data.transparencyReport}</strong>{' '}
          {textContext['accordionRow.transparencyReport.label']}
        </p>
      </ComparedDataRow>
    )

  /**
   * return mapped data rows (hospital only)
   * */
  const getHospitalDataRows = () => {
    const mappedDataRows = []

    // treatment cases //
    if (data.treatment && data.treatment.cases) {
      mappedDataRows.push(
        <ComparedDataRow
          key='treatment'
          bestValue={ compareSingleProperty(
            'treatmentCases',
            data.treatment.cases,
            comparingResults
          ) }
          title={ `${
            data.isApo
              ? textContext[
                  'compare.generalInformation.labelTreatment.stationary'
                ]
              : textContext['compare.generalInformation.labelTreatment']
          }: ` }
          hint={
            data.isApo
              ? data.generalTooltips.treatmentCasesAmbulant
              : data.generalTooltips.treatmentCases
          }>
          <p>
            <strong>{data.treatment.cases}</strong> (
            {mappedTreatmentScores[data.treatment.scoring]})
          </p>
        </ComparedDataRow>
      )
    }

    // qsr (array)
    qsrIdentifier.forEach((identifier, index) => {
      const currentQsrValue = data.qsr.filter(
        (qsrObject) => qsrObject.identifier === identifier.identifier
      )
      const currentQstValueIsBest =
        currentQsrValue.length !== 0 &&
        compareIdentifier(
          identifier.identifier,
          currentQsrValue[0].rating,
          comparingResults
        )

      if (currentQsrValue.length === 0) {
        mappedDataRows.push(
          <NoCompareValue
            key={ `qsr-${ index }` }
            compareKey={ getReplacedString({
              search: textContext['noQsrResult.placeholder'],
              replaceWith: identifier.title,
            }) }
            qsr={ true }
          />
        )
      } else {
        mappedDataRows.push(
          <ComparedDataRow
            key={ `qsr-${ index }` }
            bestValue={ compareIdentifier(
              identifier.identifier,
              currentQsrValue[0].rating,
              comparingResults
            ) }
            title={ textContext['quality.headline.qsr'] }
            hint={ data.generalTooltips.qsr }
            qsrIndicator={
              <QsrIndicator
                size={ 22 }
                className='qsr-indicator'
                currentLevel={ currentQsrValue[0].rating }
                maxLevel={ 3 }
              />
            }>
            <div className='row-item'>
              <p>{currentQsrValue[0].text.replace('.', '')}</p>
              {currentQstValueIsBest && (
                <div className={ 'checkmark-icon' }>
                  <Icon className='icon-f-checkmark' />
                </div>
              )}
            </div>
          </ComparedDataRow>
        )
      }
    })

    // iqtig (single value) //
    if (data.iqtig) {
      mappedDataRows.push(
        <ComparedDataRow
          key='iqtig'
          bestValue={ compareSingleProperty(
            'iqtig',
            data.iqtig,
            comparingResults
          ) }
          title={ `${ textContext['quality.headline.iqtig'] }:` }
          hint={ data.generalTooltips.iqtig }>
          <p>
            {data.iqtig}%{' '}
            {textContext['compare.generalInformation.iqtigIndicators']}
          </p>
        </ComparedDataRow>
      )
    } else {
      mappedDataRows.push(
        <NoCompareValue
          key='iqtig'
          compareKey={ `${ textContext['quality.headline.iqtig'] }:` }
        />
      )
    }

    // recommendation (single value)
    if (data.peq) {
      if (typeof data.peq.general !== 'number') {
        mappedDataRows.push(
          <NoCompareValue
            key='recommendation'
            compareKey={
              textContext['searchHospital.tooltip.title.recommendation']
            }
          />
        )
      } else {
        mappedDataRows.push(
          <ComparedDataRow
            key='recommendation'
            bestValue={ compareSingleProperty(
              'general',
              data.peq.general,
              comparingResults
            ) }
            title={ `${ textContext['searchHospital.tooltip.title.recommendation'] }:` }
            hint={ data.generalTooltips.recommendation }>
            <BarGraph percent={ data.peq.general } className='bar-graph' />
          </ComparedDataRow>
        )
      }
    }

    return mappedDataRows
  }

  /**
   * return complete contribution block
   */
  const getContributionDataRows = () => {
    const mappedDataRows = []

    if (data.contribution && Array.isArray(data.contribution)) {
      data.contribution.forEach((contribution, key) => {
        const { careLevelText, dataCy, hintTitle } = getContributionLabels({
          contribution,
          resultID: data.id,
          selectedType: resultType,
        })

        if (
          contribution.type === 'personalContribution' ||
          contribution.type === 'contributionDay' ||
          contribution.type === 'contributionNight'
        ) {
          mappedDataRows.push(
            <ComparedDataRow
              key={ key }
              bestValue={ compareArrayProperty(
                {
                  curKey: 'contribution',
                  curVal: contribution.amount,
                  curIndex: key,
                },
                comparingResults
              ) }
              title={ hintTitle }
              hint={ contribution?.tooltip ? contribution?.tooltip : '' }
              data-cy={ dataCy }>
              <p>
                <strong>{contribution.amount}</strong> {careLevelText}
              </p>
            </ComparedDataRow>
          )
        }

        if (contribution.type === 'personalContributionShortTerm') {
          mappedDataRows.push(
            <ComparedDataRow
              key={ `personalContributionShortTerm-${ key }` }
              bestValue={ compareArrayProperty(
                {
                  curKey: 'personalContributionShortTerm',
                  curVal: contribution.amount,
                  curIndex: key,
                  careLevel: contribution.careLevel,
                },
                comparingResults
              ) }
              withOutTooltip={ true }>
              <p>{careLevelText}</p>
            </ComparedDataRow>
          )
        }
      })
    }

    return mappedDataRows
  }

  /**
   * return complete address block
   * */
  const getAddress = () => (
    <p className='address'>
      <Styled.ContactIcon>
        <Icon className='icon-f-home' />
      </Styled.ContactIcon>
      {activeIds.length > 2 ? (
        <span className='address-split'>
          <span>
            {data.address.street.trim()}{' '}
            {data.address.streetAddition && data.address.streetAddition}
          </span>
          <span>
            {data.address.zip && data.address.zip} {data.address.city}
          </span>
        </span>
      ) : (
        <>
          {`${ data.address.street }${
            data.address?.streetAddition
              ? ` ${ data.address.streetAddition }`
              : ''
          }, ${ data.address.zip } ${ data.address.city }`}
        </>
      )}
    </p>
  )

  /**
   * link to detail profile
   **/
  const getDetailLink = () => {
    const urlParams = {
      id: data.id,
      entryPoint: resultType,
      city: data.address.city,
      zip: data.address.zip,
      key: !withOutTreatmentReason
        ? bookmarks.types[resultType].searchKey
        : false,
      type: !withOutTreatmentReason ? bookmarks.types[resultType].type : false,
      searchTerm: !withOutTreatmentReason
        ? bookmarks.types[resultType].searchTerm
        : false,
    }

    return getDetailURL(urlParams)
  }

  return (
    <Styled.GeneralInformationCompare className={ wrapperClass }>
      {type === 'header' && (
        <div className='item-header'>
          {data?.cooperation && (
            <p className={ 'cooperation' }>{data.cooperation}</p>
          )}
          <Link to={ getDetailLink() } title={ data.locationName }>
            <h2
              className={ `h2 headline-alternative ${
                data.subjectArea ? 'has-subjectArea' : ''
              }` }
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(data.locationName),
              }}
            />
          </Link>
          {data.subjectArea && (
            <p className={ 'subjectArea' }>{data.subjectArea}</p>
          )}
          {resultType !== entryPointId.DOCTORS && data.address && getAddress()}
          <BookmarkToggle
            className={ `bookmark-general-information ${
              mobile ? 'mobile' : null
            }` }
            selected={ true }
            onToggle={ () => onToggleBookmark({ id: data.id, type: resultType }) }
            disabled={ disableToggleBookmark }
          />
        </div>
      )}

      {type === 'compare' && (
        <div className='data-container'>
          {resultType === entryPointId.HOSPITALS && getHospitalDataRows()}

          {(resultType === entryPointId.CARESERVICES ||
            resultType === entryPointId.CAREHOMES) &&
            getTransparencyReport()}

          {/* TODO contribution compare for care services currently not possible, brocken data */}
          {RESULT_HAS_CONTRIBUTION.includes(resultType) &&
            resultType !== entryPointId.CARESERVICES &&
            getContributionDataRows()}
        </div>
      )}

      {type === 'contact' && hasContactData && (
        <div
          className={ `contact-information ${
            resultType === entryPointId.HOSPITALS ? 'hospitals' : 'standard'
          }` }>
          {/* address */}
          {resultType === entryPointId.DOCTORS && data?.address && getAddress()}

          {/* phone */}
          {data?.phone && (
            <p>
              <Styled.ContactIcon>
                <Icon className='icon-f-phone-handset' />
              </Styled.ContactIcon>
              <a className='phone' href={ `tel:${ data.phone }` }>
                {data.phone}
              </a>
            </p>
          )}

          {/* fax */}
          {data?.fax && (
            <p>
              <Styled.ContactIcon>
                <Icon className='icon-f-printer' />
              </Styled.ContactIcon>
              {data.fax}
            </p>
          )}

          {/* mail */}
          {data?.email && (
            <p>
              <Styled.ContactIcon>
                <Icon className='icon-f-envelope' />
              </Styled.ContactIcon>
              <a
                href={ `mailto:${ data.email }` }
                target='_blank'
                rel='noopener noreferrer'>
                {data.email}
              </a>
            </p>
          )}

          {/* url */}
          {data?.web && (
            <p>
              <Styled.ContactIcon>
                <Icon className='icon-f-video-chat' />
              </Styled.ContactIcon>
              <a href={ `${ data.web }` } target='_blank' rel='noopener noreferrer'>
                {data.web}
              </a>
            </p>
          )}
        </div>
      )}
    </Styled.GeneralInformationCompare>
  )
}

GeneralInformationCompare.defaultProps = {
  resultType: 'doctors',
  type: 'header',
  withOutTreatmentReason: false,
  mobile: false,
  disableToggleBookmark: false,
}

GeneralInformationCompare.propTypes = {
  type: oneOf(['header', 'contact', 'compare']).isRequired,
  resultType: oneOf([...BOOKABLE_TYPES]),
  data: shape({
    address: shape({
      street: string.isRequired,
      streetAddition: string,
      zip: string.isRequired,
      city: string.isRequired,
    }).isRequired,
    treatment: shape({
      cases: number,
      scoring: string,
    }),
    qsr: arrayOf(
      shape({
        identifier: string,
        indicators: array, // single indicators
        info: string,
        rating: number,
        sorting: number,
        status: number,
        text: string,
        title: string,
        tooltip: string,
      })
    ),
    peq: shape({
      general: number,
      cases: number,
      average: number,
    }),
    generalTooltips: oneOfType([
      bool,
      shape({
        iqtig: string,
        qsr: string,
        treatmentCases: string,
        treatmentCasesAmbulant: string,
        recommendation: string,
      }),
    ]),
    locationName: string.isRequired,
    phone: string,
    fax: string,
    email: string,
    web: string,
  }).isRequired,
  onToggleBookmark: func,
  isBookmarked: bool,
  children: node,
  height: oneOfType([number, string]),
  withOutTreatmentReason: bool,
  mobile: bool,
  className: string,
  disableToggleBookmark: bool,
}

export default GeneralInformationCompare
